import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Active conversation ID
    activeConversationId: null,

    // Conversations list (from Firestore listener)
    conversations: [],

    // Messages by conversation ID
    messagesByConversation: {},

    // Presence data by user ID
    presenceByUserId: {},

    // Typing indicators by conversation ID
    typingByConversation: {},

    // Members by conversation ID (for groups)
    membersByConversation: {},

    // Reply, Edit, Forward state
    replyingTo: null,
    editingMessage: null,
    forwardingMessage: null,
    highlightedMessageId: null,

    // Pagination
    hasMoreByConversation: {},
    lastDocsByConversation: {},
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        // Set active conversation
        setActiveConversationId: (state, action) => {
            state.activeConversationId = action.payload;
        },

        // FIX M-9: Shallow-compare conversations before replacing to prevent
        // unnecessary re-renders when data hasn't actually changed
        setConversations: (state, action) => {
            const newConvs = action.payload;
            const oldConvs = state.conversations;
            // Fast path: different length → always replace
            if (newConvs.length !== oldConvs.length) {
                state.conversations = newConvs;
                return;
            }
            // Compare by key fields that affect UI rendering (C7: added lastSeenAt)
            const changed = newConvs.some((nc, i) => {
                const oc = oldConvs[i];
                if (!oc || nc.id !== oc.id) return true;
                const nLMA = nc.lastMessageAt?.toMillis?.() || nc.lastMessageAt?.getTime?.() || nc.lastMessageAt;
                const oLMA = oc.lastMessageAt?.toMillis?.() || oc.lastMessageAt?.getTime?.() || oc.lastMessageAt;
                const nLSA = nc.lastSeenAt ? JSON.stringify(nc.lastSeenAt) : null;
                const oLSA = oc.lastSeenAt ? JSON.stringify(oc.lastSeenAt) : null;
                return nLMA !== oLMA || nc.lastMessageText !== oc.lastMessageText || nLSA !== oLSA;
            });
            if (changed) {
                state.conversations = newConvs;
            }
        },

        // Set messages for a conversation
        setMessages: (state, action) => {
            const { conversationId, messages } = action.payload;
            state.messagesByConversation[conversationId] = messages;
        },

        // Set presence data
        setPresenceByUserId: (state, action) => {
            state.presenceByUserId = action.payload;
        },

        // Set typing state for a conversation
        setTypingByConversation: (state, action) => {
            const { conversationId, typingUsers } = action.payload;
            state.typingByConversation[conversationId] = typingUsers;
        },

        // Set members for a conversation (C1: wired up — ChatWindow dispatches this)
        setMembersByConversation: (state, action) => {
            const { conversationId, members } = action.payload;
            state.membersByConversation[conversationId] = members;
        },

        // Reply state
        setReplyingTo: (state, action) => {
            state.replyingTo = action.payload;
        },

        // Edit state
        setEditingMessage: (state, action) => {
            state.editingMessage = action.payload;
        },

        // Forward state
        setForwardingMessage: (state, action) => {
            state.forwardingMessage = action.payload;
        },

        // Highlighted message for scroll-to-reply
        setHighlightedMessage: (state, action) => {
            state.highlightedMessageId = action.payload;
        },

        // Has more for pagination
        setHasMore: (state, action) => {
            const { conversationId, hasMore } = action.payload;
            state.hasMoreByConversation[conversationId] = hasMore;
        },

        // FIX M-15: Store serializable cursor instead of raw DocumentSnapshot
        setLastDoc: (state, action) => {
            const { conversationId, lastDoc } = action.payload;
            // Extract the createdAt Timestamp for serializable pagination cursor
            const createdAt = lastDoc?.data?.()?.createdAt || null;
            state.lastDocsByConversation[conversationId] = createdAt;
        },

        // Update a single conversation (for optimistic updates)
        updateConversation: (state, action) => {
            const { conversationId, updates } = action.payload;
            const index = state.conversations.findIndex(c => c.id === conversationId);
            if (index !== -1) {
                state.conversations[index] = { ...state.conversations[index], ...updates };
            }
        },

        // Reset chat state (on logout)
        resetChatState: () => initialState,
    },
});

export const {
    setActiveConversationId,
    setConversations,
    setMessages,
    setPresenceByUserId,
    setTypingByConversation,
    setMembersByConversation,
    setReplyingTo,
    setEditingMessage,
    setForwardingMessage,
    setHighlightedMessage,
    setHasMore,
    setLastDoc,
    updateConversation,
    resetChatState,
} = chatSlice.actions;

export default chatSlice.reducer;
